import { TranslateOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Result from 'components/v2/common/Result';
import { ProfileFields } from './profileFields';
import * as S from '../ClientProfile.styled';
import { LanguageType } from '../ClientProfile';

import { RootState } from 'redux/store';

type LanguageProps = {
    languageData: LanguageType[] | null;
    spouseLanguageData: LanguageType[] | null;
    mobile: boolean;
    spouseOnly?: boolean;
};

const LanguageProficiency = ({
    languageData,
    spouseLanguageData,
    mobile,
    spouseOnly = false,
}: LanguageProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const hasLanguageData = !spouseOnly && !!languageData?.length;
    const hasSpouseLanguageData = !!spouseLanguageData?.length;

    return (
        <>
            {hasLanguageData ? (
                <>
                    {hasSpouseLanguageData && (
                        <S.Title>{t('applicant', { lng: language })}</S.Title>
                    )}

                    {languageData?.map((lang: LanguageType, index: number) => (
                        <div key={index}>
                            <S.InfoItem>
                                <Tooltip title={t('language', { lng: language })}>
                                    <S.Label>{t('language', { lng: language })}</S.Label>
                                </Tooltip>
                                <Tooltip title={lang.language}>
                                    <S.Info mobile={mobile}>{lang.language}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('testType', { lng: language })}>
                                    <S.Label>{t('testType', { lng: language })}</S.Label>
                                </Tooltip>
                                <Tooltip title={lang.test_type}>
                                    <S.Info mobile={mobile}>{lang.test_type}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('listeningScore', { lng: language })}>
                                    <S.Label>
                                        {mobile
                                            ? t('listening', { lng: language })
                                            : t('listeningScore', { lng: language })}
                                    </S.Label>
                                </Tooltip>
                                <Tooltip title={lang.listening}>
                                    <S.Info mobile={mobile}>{lang.listening}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('readingScore', { lng: language })}>
                                    <S.Label>
                                        {mobile
                                            ? t('reading', { lng: language })
                                            : t('readingScore', { lng: language })}
                                    </S.Label>
                                </Tooltip>
                                <Tooltip title={lang.reading}>
                                    <S.Info mobile={mobile}>{lang.reading}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('writingScore', { lng: language })}>
                                    <S.Label>
                                        {mobile
                                            ? t('writing', { lng: language })
                                            : t('writingScore', { lng: language })}
                                    </S.Label>
                                </Tooltip>
                                <Tooltip title={lang.writing}>
                                    <S.Info mobile={mobile}>{lang.writing}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('speakingScore', { lng: language })}>
                                    <S.Label>
                                        {mobile
                                            ? t('speaking', { lng: language })
                                            : t('speakingScore', { lng: language })}
                                    </S.Label>
                                </Tooltip>
                                <Tooltip title={lang.speaking}>
                                    <S.Info mobile={mobile}>{lang.speaking}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <ProfileFields
                                record={lang}
                                keys={['level', 'test_date']}
                                mobile={mobile}
                            />
                        </div>
                    ))}
                </>
            ) : !hasSpouseLanguageData ? (
                <Result
                    icon={<TranslateOutlined sx={{ fontSize: 32 }} />}
                    title={t('noInfo', { lng: language })}
                    description="No language proficiency details have been added yet."
                />
            ) : null}
            {hasSpouseLanguageData && (
                <>
                    {!spouseOnly && <S.Title>{t('partner', { lng: language })}</S.Title>}
                    {spouseLanguageData?.map((lang: LanguageType, index: number) => (
                        <div key={index}>
                            <S.InfoItem>
                                <Tooltip title={t('language', { lng: language })}>
                                    <S.Label>{t('language', { lng: language })}</S.Label>
                                </Tooltip>
                                <Tooltip title={lang.language}>
                                    <S.Info mobile={mobile}>{lang.language}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('testType', { lng: language })}>
                                    <S.Label>{t('testType', { lng: language })}</S.Label>
                                </Tooltip>
                                <Tooltip title={lang.test_type}>
                                    <S.Info mobile={mobile}>{lang.test_type}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('listeningScore', { lng: language })}>
                                    <S.Label>
                                        {mobile
                                            ? t('listening', { lng: language })
                                            : t('listeningScore', { lng: language })}
                                    </S.Label>
                                </Tooltip>
                                <Tooltip title={lang.listening}>
                                    <S.Info mobile={mobile}>{lang.listening}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('readingScore', { lng: language })}>
                                    <S.Label>
                                        {mobile
                                            ? t('reading', { lng: language })
                                            : t('readingScore', { lng: language })}
                                    </S.Label>
                                </Tooltip>
                                <Tooltip title={lang.reading}>
                                    <S.Info mobile={mobile}>{lang.reading}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('writingScore', { lng: language })}>
                                    <S.Label>
                                        {mobile
                                            ? t('writing', { lng: language })
                                            : t('writingScore', { lng: language })}
                                    </S.Label>
                                </Tooltip>
                                <Tooltip title={lang.writing}>
                                    <S.Info mobile={mobile}>{lang.writing}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('speakingScore', { lng: language })}>
                                    <S.Label>
                                        {mobile
                                            ? t('speaking', { lng: language })
                                            : t('speakingScore', { lng: language })}
                                    </S.Label>
                                </Tooltip>
                                <Tooltip title={lang.speaking}>
                                    <S.Info mobile={mobile}>{lang.speaking}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                        </div>
                    ))}
                </>
            )}
        </>
    );
};

export default LanguageProficiency;
